import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as authApi from '../../api/authApi';
import { useAuth } from '../../auth/AuthProvider';
import { workspaceHomePath } from '../../auth/workspaces';
import { UmbLogo } from '../../components/layout/UmbLogo';

export function AcceptInvitePage() {
  const { token: routeToken } = useParams();
  const navigate = useNavigate();
  const { applyTokenPair, jwtMode } = useAuth();
  const [token, setToken] = useState(routeToken || '');
  const [preview, setPreview] = useState(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadingPreview(true);
    authApi
      .previewInvitation(token)
      .then((data) => {
        if (!cancelled) {
          setPreview(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview(null);
          setError(err.message || 'Invitation not found');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleAccept(event) {
    event.preventDefault();
    if (!token) {
      setError('Invite token is required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const tokenPair = await authApi.acceptInvitation(token, {
        fullName: fullName.trim(),
        password,
      });
      if (jwtMode) {
        const next = applyTokenPair(tokenPair);
        navigate(workspaceHomePath(next.workspaceId));
      } else {
        navigate('/signin');
      }
    } catch (err) {
      setError(err.message || 'Could not accept invitation');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-eorig-surface px-4 py-10 font-sans text-eorig-ink">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-eorig-border bg-white p-6 shadow-eorig-card sm:p-8">
          <UmbLogo />
          <h1 className="mt-6 text-2xl font-semibold tracking-tight">Accept invitation</h1>
          <p className="mt-2 text-sm text-eorig-muted">
            Create your password to join the organization workspace.
          </p>

          {!routeToken ? (
            <label className="mt-4 block text-sm">
              <span className="mb-1.5 block font-medium">Invite token</span>
              <input
                value={token}
                onChange={(e) => setToken(e.target.value.trim())}
                className="w-full rounded-xl border border-eorig-border px-3 py-2.5 text-sm"
                placeholder="Paste invite token"
              />
            </label>
          ) : null}

          {loadingPreview ? (
            <p className="mt-4 text-sm text-eorig-muted">Loading invitation…</p>
          ) : null}

          {preview ? (
            <div className="mt-4 rounded-xl border border-eorig-border bg-eorig-surface px-3 py-3 text-sm">
              <div className="font-semibold">{preview.organization_name}</div>
              <div className="mt-1 text-eorig-muted">
                {preview.org_type} · {preview.role} · {preview.email}
              </div>
            </div>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={handleAccept}>
            <label className="block text-sm">
              <span className="mb-1.5 block font-medium">Full name</span>
              <input
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-xl border border-eorig-border px-3 py-2.5 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1.5 block font-medium">Password</span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-eorig-border px-3 py-2.5 text-sm"
              />
            </label>
            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy || !token}
              className="w-full rounded-xl bg-umb-navy px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? 'Accepting…' : 'Accept and continue'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-eorig-muted">
            Already have an account?{' '}
            <Link to="/signin" className="font-medium text-eorig-cyan underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
